import { Component, EventEmitter, Output, ChangeDetectionStrategy } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { TooltipConfig } from '../../models/tooltip.model';
import { TooltipPosition, TooltipAlignment, TooltipIconMode } from '../../models/tooltip.enum';

@Component({
  selector: 'app-tooltip-config-modal',
  templateUrl: './tooltip-config-modal.component.html',
  styleUrl: './tooltip-config-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class TooltipConfigModalComponent {
  @Output() onSave = new EventEmitter<Partial<TooltipConfig>>();

  readonly TooltipPosition = TooltipPosition;
  readonly TooltipAlignment = TooltipAlignment;
  readonly TooltipIconMode = TooltipIconMode;

  header: string = '';
  text: string = '';
  position: TooltipPosition = TooltipPosition.Bottom;
  tooltipAlign: TooltipAlignment = TooltipAlignment.Center;
  iconPlacement: TooltipPosition = TooltipPosition.Right;
  iconPlacementMode: TooltipIconMode = TooltipIconMode.Inline;
  iconAlign: TooltipAlignment = TooltipAlignment.Center;
  iconRotation: number = 0;
  iconOffsetTop: number = 0;
  iconOffsetLeft: number = 0;
  isEdit: boolean = false;
  tooltipId?: string;

  positionOptions = [
    { value: TooltipPosition.Top, label: 'Top' },
    { value: TooltipPosition.Bottom, label: 'Bottom' },
    { value: TooltipPosition.Left, label: 'Left' },
    { value: TooltipPosition.Right, label: 'Right' }
  ];

  iconPlacementOptions = [
    { value: TooltipPosition.Top, label: 'Top' },
    { value: TooltipPosition.Bottom, label: 'Bottom' },
    { value: TooltipPosition.Left, label: 'Left' },
    { value: TooltipPosition.Right, label: 'Right' }
  ];

  iconAlignOptions: { value: TooltipAlignment; label: string }[] = [];
  tooltipAlignOptions: { value: TooltipAlignment; label: string }[] = [];

  iconRotationOptions = [0, 45, 90, 135, 180, 225, 270, 315, 360];

  constructor(public bsModalRef: BsModalRef) {}

  ngOnInit(): void {
    this.updateIconAlignOptions();
    this.updateTooltipAlignOptions();
  }

  onPositionChange(): void {
    this.updateTooltipAlignOptions();
  }

  onPlacementChange(): void {
    this.updateIconAlignOptions();
  }

  updateIconAlignOptions(): void {
    this.updateAlignOptions(this.iconPlacement, 'icon');
  }

  updateTooltipAlignOptions(): void {
    this.updateAlignOptions(this.position, 'tooltip');
  }

  private updateAlignOptions(position: TooltipPosition, target: 'icon' | 'tooltip'): void {
    const isHorizontal = position === TooltipPosition.Top || position === TooltipPosition.Bottom;
    const options = isHorizontal
      ? [
          { value: TooltipAlignment.Left, label: 'Left' },
          { value: TooltipAlignment.Center, label: 'Center' },
          { value: TooltipAlignment.Right, label: 'Right' }
        ]
      : [
          { value: TooltipAlignment.Top, label: 'Top' },
          { value: TooltipAlignment.Center, label: 'Center' },
          { value: TooltipAlignment.Bottom, label: 'Bottom' }
        ];

    const validAlignments = isHorizontal
      ? [TooltipAlignment.Left, TooltipAlignment.Center, TooltipAlignment.Right]
      : [TooltipAlignment.Top, TooltipAlignment.Center, TooltipAlignment.Bottom];

    if (target === 'icon') {
      this.iconAlignOptions = options;
      if (!validAlignments.includes(this.iconAlign)) {
        this.iconAlign = TooltipAlignment.Center;
      }
    } else {
      this.tooltipAlignOptions = options;
      if (!validAlignments.includes(this.tooltipAlign)) {
        this.tooltipAlign = TooltipAlignment.Center;
      }
    }
  }

  save(): void {
    if (!this.header?.trim() || !this.text?.trim()) {
      alert('Please enter a header and text');
      return;
    }

    const config: Partial<TooltipConfig> = {
      header: this.header.trim(),
      text: this.text.trim(),
      position: this.position,
      tooltipAlign: this.tooltipAlign,
      iconPlacement: this.iconPlacement,
      iconPlacementMode: this.iconPlacementMode,
      iconAlign: this.iconAlign,
      iconRotation: this.iconRotation,
      iconOffsetTop: this.iconOffsetTop,
      iconOffsetLeft: this.iconOffsetLeft
    };

    if (this.tooltipId) config.id = this.tooltipId;

    this.onSave.emit(config);
    this.bsModalRef.hide();
  }

  cancel(): void {
    this.bsModalRef.hide();
  }
}
